import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Heart, Layers, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { values } from "../mock";
import useReveal from "../hooks/useReveal";

const blocks = [
  {
    icon: Target,
    title: "Our Vision",
    text: "To become the go-to IT, AV and Security services partner for growing businesses — delivering exceptional service that helps clients stay ahead in an ever-evolving world.",
    image: "https://img1.wsimg.com/isteam/stock/74o9Eon/:/rs=w:900,cg:true,m",
  },
  {
    icon: Heart,
    title: "Our Values",
    text: "Honesty, integrity and accountability. We build long-lasting partnerships through trust, innovation and continuous improvement in everything we deliver.",
    image: "https://img1.wsimg.com/isteam/stock/103734/:/rs=w:900,cg:true,m",
  },
  {
    icon: Layers,
    title: "Our Services",
    text: "A wide range of IT, AV and Security services — from network setup and maintenance to cloud solutions and cybersecurity — tailored to your needs and budget.",
    image: "https://img1.wsimg.com/isteam/stock/yra0KDP/:/rs=w:900,cg:true,m",
  },
];

export default function About() {
  useReveal();
  return (
    <div>
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 bg-grid bg-grid-fade opacity-50" />
        <div className="absolute -top-40 -left-20 h-96 w-96 rounded-full bg-sky-500/25 blur-3xl animate-float-slow" />
        <div className="absolute -bottom-40 right-0 h-96 w-96 rounded-full bg-cyan-400/20 blur-3xl animate-float-slower" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="reveal max-w-3xl">
            <div className="inline-flex items-center gap-2 text-sky-300 font-semibold text-xs tracking-[0.25em] uppercase">
              <Sparkles className="h-3.5 w-3.5" /> About us
            </div>
            <h1 className="font-display mt-4 text-5xl lg:text-7xl font-bold tracking-tight leading-[1.02]">
              The engineering partner behind{" "}
              <span className="bg-gradient-to-r from-sky-300 to-cyan-300 bg-clip-text text-transparent">
                resilient workplaces.
              </span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
              We're well-versed across IT, audio, video, security systems,
              networks and databases — helping small and mid-sized businesses
              turn technology into a competitive advantage.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-3 gap-6">
          {values.map((v, i) => (
            <div
              key={v.title}
              className="reveal group relative bg-slate-50 hover:bg-white rounded-2xl p-8 border border-slate-100 hover:border-sky-300 hover:shadow-xl hover:shadow-sky-500/10 hover:-translate-y-1 transition-all duration-500"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 text-white shadow-lg shadow-sky-500/30 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <h3 className="font-display mt-6 text-xl font-semibold text-slate-900">
                {v.title}
              </h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                {v.text}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 space-y-24">
          {blocks.map((b, i) => (
            <div
              key={b.title}
              className={`reveal grid lg:grid-cols-2 gap-10 items-center ${
                i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""
              }`}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-tr from-sky-100/60 to-cyan-50 rounded-3xl blur-xl" />
                <div className="relative rounded-2xl overflow-hidden shadow-xl ring-1 ring-slate-200 group">
                  <img
                    src={b.image}
                    alt={b.title}
                    className="w-full h-[380px] object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
              <div>
                <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 text-white shadow-lg shadow-sky-500/30 flex items-center justify-center">
                  <b.icon className="h-6 w-6" />
                </div>
                <h2 className="font-display mt-5 text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
                  {b.title}
                </h2>
                <p className="mt-5 text-slate-600 leading-relaxed text-lg">{b.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="reveal relative overflow-hidden rounded-3xl bg-slate-950 text-white p-12 lg:p-16">
            <div className="absolute inset-0 bg-grid bg-grid-fade opacity-50" />
            <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-sky-500/25 blur-3xl" />
            <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div className="max-w-2xl">
                <h3 className="font-display text-3xl lg:text-4xl font-bold tracking-tight leading-tight">
                  Ready to talk technology?
                </h3>
                <p className="mt-4 text-slate-300">
                  Whether you're planning a new site rollout or looking for a
                  dependable AMC partner — we'd love to chat.
                </p>
              </div>
              <div className="flex gap-3">
                <Link to="/services">
                  <Button variant="outline" className="h-11 rounded-md border-white/20 bg-transparent text-white hover:bg-white/10">
                    Our Services
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button className="bg-sky-500 hover:bg-sky-400 text-white h-11 rounded-md">
                    Contact Us <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
